import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { loginSchema } from '@faktorplus/shared';
import { useAuth } from './auth-provider';
import { Button } from '../components/ui/button';
import { Field, Input } from '../components/ui/form';
import { ErrorBox, LoadingBlock } from '../components/ui/feedback';
import { ThemeToggle } from '../theme/theme-toggle';
import { ApiError } from '../lib/api-client';
import styles from './login-page.module.css';

/** Demo accounts, so the client can switch roles during the presentation. */
const DEMO_ACCOUNTS = [
  { role: 'Właściciel', email: 'marcin@faktorplus.pl' },
  { role: 'Księgowość', email: 'ewa@faktorplus.pl' },
  { role: 'Kierownik', email: 'bartek@faktorplus.pl' },
];
const DEMO_PASSWORD = 'faktorplus2026';

export function LoginPage() {
  const { user, isLoading, login } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) return <LoadingBlock />;

  if (user) {
    const target = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={target} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    setIsSubmitting(true);
    try {
      await login(parsed.data);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Nie udało się zalogować. Spróbuj ponownie.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function fillDemoAccount(demoEmail: string) {
    setEmail(demoEmail);
    setPassword(DEMO_PASSWORD);
    setError(null);
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.head}>
          <div>
            <div className={styles.brand}>
              Faktor<em>Plus</em>
            </div>
            <p className={styles.subtitle}>Obieg faktur</p>
          </div>
          <ThemeToggle />
        </div>

        <form className={styles.form} onSubmit={(event) => void handleSubmit(event)}>
          <Field label="Adres e-mail">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                autoComplete="username"
                autoFocus
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          <Field label="Hasło">
            {(id) => (
              <Input
                id={id}
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          {error && <ErrorBox>{error}</ErrorBox>}

          <Button type="submit" variant="primary" large isLoading={isSubmitting}>
            Zaloguj się
          </Button>
        </form>

        <div className={styles.demo}>
          <p className={styles.demoTitle}>Konta demonstracyjne</p>
          <div className={styles.demoList}>
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                className={styles.demoRow}
                onClick={() => fillDemoAccount(account.email)}
              >
                <span className={styles.demoRole}>{account.role}</span>
                <span className={styles.demoEmail}>{account.email}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
